import { useEffect, useId, useRef, useState } from "react";
import { api } from "../api/client";
import type { LocationInput as Location, PlaceSuggestion } from "../api/types";
import { loadMapsLibrary } from "../googleMaps";

const DEBOUNCE_MS = 350;
const MIN_QUERY = 3;

/** A suggestion that could come from either Google or Nominatim. */
interface Suggestion {
  id: string;
  label: string;
  secondary: string;
  kind: string;
  /** Google PlacePrediction object for resolving to coordinates. */
  prediction: google.maps.places.PlacePrediction | null;
  /** Nominatim result with coordinates already resolved. */
  nominatim: PlaceSuggestion | null;
}

interface Props {
  value: string;
  onChange: (text: string) => void;
  onResolve: (location: Location, label: string) => void;
  disabled?: boolean;
  placeholder?: string;
  near?: { lat: number; lng: number };
  apiKey: string | null;
}

/**
 * Type-ahead for a person's location.
 *
 * When a Google API key is available, uses the new AutocompleteSuggestion API
 * (replacing the deprecated AutocompleteService) for much better address
 * resolution. Falls back to Nominatim when no key is configured.
 */
export function LocationInput({
  value,
  onChange,
  onResolve,
  disabled,
  placeholder,
  near,
  apiKey,
}: Props) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const listId = useId();
  const chosen = useRef(false);
  const googleReady = useRef(false);

  // Bootstrap Google Places when apiKey is available
  useEffect(() => {
    if (!apiKey || googleReady.current) return;
    let cancelled = false;
    loadMapsLibrary(apiKey)
      .then(() => window.google!.maps!.importLibrary!("places"))
      .then(() => {
        if (!cancelled) googleReady.current = true;
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [apiKey]);

  useEffect(() => {
    if (chosen.current) {
      chosen.current = false;
      return;
    }
    const query = value.trim();
    if (query.length < MIN_QUERY) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const timer = setTimeout(async () => {
      try {
        let results: Suggestion[];

        if (googleReady.current && google?.maps?.places?.AutocompleteSuggestion) {
          results = await googleAutocomplete(query, near);
        } else {
          results = await nominatimAutocomplete(query, near);
        }

        if (cancelled) return;
        setSuggestions(results);
        setOpen(results.length > 0);
        setActive(-1);
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      setLoading(false);
    };
  }, [value, near?.lat, near?.lng]);

  async function choose(suggestion: Suggestion) {
    chosen.current = true;
    onChange(suggestion.label);
    setOpen(false);
    setSuggestions([]);

    if (suggestion.prediction) {
      // Resolve Google prediction to exact coordinates via the new Place API
      try {
        const place = suggestion.prediction.toPlace();
        await place.fetchFields({ fields: ["location"] });
        if (place.location) {
          onResolve(
            { point: { lat: place.location.lat(), lng: place.location.lng() } },
            suggestion.label,
          );
          return;
        }
      } catch {
        // Fall through to area fallback
      }
    }

    if (suggestion.nominatim) {
      onResolve({ place_id: suggestion.nominatim.place_id }, suggestion.label);
    } else {
      onResolve({ area: suggestion.label }, suggestion.label);
    }
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (!open || suggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (event.key === "Enter" && active >= 0) {
      event.preventDefault();
      const picked = suggestions[active];
      if (picked) void choose(picked);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="combobox">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        placeholder={placeholder ?? "Neighbourhood or address"}
        aria-label="Location"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        role="combobox"
        disabled={disabled}
        autoComplete="off"
      />
      {loading && <span className="spinner" aria-hidden="true" />}

      {open && (
        <ul className="suggestions" id={listId} role="listbox">
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.id} role="option" aria-selected={index === active}>
              <button
                type="button"
                className={index === active ? "suggestion active" : "suggestion"}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void choose(suggestion)}
              >
                <span className="suggestion-label">{suggestion.label}</span>
                <span className="suggestion-kind">{suggestion.secondary || suggestion.kind}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Google Places Autocomplete via the new AutocompleteSuggestion API. */
async function googleAutocomplete(
  query: string,
  near?: { lat: number; lng: number },
): Promise<Suggestion[]> {
  const request: google.maps.places.AutocompleteRequest = {
    input: query,
    includedRegionCodes: ["in"],
  };
  if (near) {
    request.locationBias = new google.maps.Circle({
      center: { lat: near.lat, lng: near.lng },
      radius: 30000,
    });
  }

  const { suggestions } =
    await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

  return suggestions
    .filter((s): s is typeof s & { placePrediction: google.maps.places.PlacePrediction } =>
      s.placePrediction != null,
    )
    .slice(0, 6)
    .map((s) => {
      const p = s.placePrediction;
      return {
        id: p.placeId,
        label: p.mainText?.toString() ?? p.text?.toString() ?? query,
        secondary: p.secondaryText?.toString() ?? "",
        kind: p.types?.includes("sublocality") ? "area" : "address",
        prediction: p,
        nominatim: null,
      };
    });
}

/** Nominatim fallback — used when no Google API key is configured. */
async function nominatimAutocomplete(
  query: string,
  near?: { lat: number; lng: number },
): Promise<Suggestion[]> {
  const found = await api.autocomplete(query, undefined, near);
  return found.map((s) => ({
    id: s.place_id,
    label: s.label,
    secondary: "",
    kind: s.kind.toUpperCase(),
    prediction: null,
    nominatim: s,
  }));
}
